import moment from 'moment';
import { store } from '../store';
import { updateEnrollment } from '../actions/enrollment';
import { evaluateConditions } from '../common/utils/enrollment';

export function getPageFromUrl(form, url) {
  const urlParts = url.split('/');
  let returnPage;
  form.forEach((section) => {
    if (section.route === `/${urlParts.at(-2)}`) {
      section.pages.forEach(page => {
        if (page.route === `/${urlParts.at(-1)}`) {
          returnPage = page;
        }
      });
    }
  });
  return returnPage;
}

export function getAllFields(form) {
  const fields = [];
  form.forEach(section => {
    section.pages.forEach(page => {
      page.fields.forEach(field => fields.push(field));
    });
  });
  return fields;
}

export function getRoutes(location) {
  // takes the location object returned from react-router-dom useLocation
  const l = location.pathname.split('/');
  // l should look like:
  // [ '', 'enroll', 'Aetna', '2023', 'acknowledge', 'recording' ]
  return {
    carrier: `/${l[2]}`,
    year: `/${l[3]}`,
    currentSection: `/${l[4]}`,
    currentPage: `/${l[5]}`,
  };
}

export function getSection(location) {
  // takes the location object returned from react-router-dom useLocation
  const { form } = store.getState().form;
  const { currentSection } = getRoutes(location);
  return form.find(section => section.route === currentSection);
}

export function getAllRoutes(form, baseRoute) {
  const urls = [];
  const formRoute = `/enroll${baseRoute}`;
  form.forEach(section => {
    const sectionRoute = section.route;
    section.pages.forEach(page => {
      urls.push(`${formRoute}${sectionRoute}${page.route}`);
    });
  });
  return urls;
}

export function getField(form, name) {
  let rfield = false;
  form.forEach(section => {
    section.pages.forEach(page => {
      page.fields.forEach(field => {
        if (field.name === name) {
          rfield = field;
        }
      });
    });
  });
  return rfield;
}

export function fieldExistsInConfig(form, name) {
  const fields = getAllFields(form);
  let fieldExists = false;
  fields.forEach(field => {
    if (field.name === name) fieldExists = true;
  });
  return fieldExists;
}

export function fieldShouldDisplay(form, name, currentValues) {
  // first check if field exists in config
  let shouldDisplay = fieldExistsInConfig(form, name);
  const field = getField(form, name);
  // if the field is conditionally displayed check it's conditions
  if (Array.isArray(field.displayConditions)) {
    shouldDisplay = evaluateConditions({ currentValues, conditions: field.displayConditions });
  }
  return shouldDisplay;
}

export function getFieldKey(form, name) {
  // fields have a shape of
  // {
  //   name: 'home_address',
  //   required: true|false,
  //   (optional) label: 'Home Address',
  //   ... etc
  // }
  const field = getField(form, name);
  return field.label;
}

export function getOptions(form, name) {
  // returns either `false` or an array in the shape
  // [
  //   {label: 'field label', value: 'value'},
  //   {label: 'field label', value: 'value'},
  //   &etc...
  // ]
  let options = false;
  const field = getField(form, name);
  if (field.options) {
    options = field.options;
  }
  return options;
}

export function extractValidation(formConfig) {
  const fields = getAllFields(formConfig.form);
  const validation = {};
  fields.forEach(field => {
    if (field.validation) {
      validation[field.name] = field.validation;
    }
  });
  return validation;
}

export function isRequired({ form, name, currentValues }) {
  let required = false;
  const field = getField(form, name);
  if (field.requiredConditions && currentValues) {
    required = evaluateConditions({
      currentValues,
      conditions: field.requiredConditions,
    });
  } else if (field.required) {
    required = true;
  }
  return required;
}

export function getValidClass({ valid, required, value }) {

  let classText = '';

  // checks for non-required fields
  if (!required && (value === '' || value === undefined)) {
    classText = '';
  } else if (!required && valid && valid !== undefined) {
    classText = 'is-valid';
  } else if (!required && !valid && valid !== undefined) {
    classText = 'is-invalid';

  // checks for required fields
  } else if (valid !== undefined && valid) {
    classText = 'is-valid';
  } else if (valid !== undefined && !valid) {
    classText = 'is-invalid';
  }
  return classText;
}

export function isValid({ value, validationRegex }) {
  // for values that are arrays (I believe this is only true for MultiCheck
  // inputs) we test whether it's valid by whethere there is 1 or more
  // items in the array.
  if (Array.isArray(value)) {
    if (value.length) return true;
    if (value.length <= 0) return false;
  }

  // re.test(val) will convert val to a string (e.g. if val is undefined
  // it will cast val to a string 'undefined'). If value is undefined
  // we should test an empty string.
  let testVal = '';
  if (value !== undefined) testVal = value;

  const re = new RegExp(validationRegex);
  return re.test(testVal);
}

// eslint-disable-next-line
export function handleValidation({ value, valuesArray, validationRegex, name, required }) {
  // Function should take EITHER a value arg OR a valuesArray arg, never both.
  // If we have a values array, it is valid if it contains 1 or more items. It
  // is invalid if it is empty.

  // if valuesArray is provided
  if (Array.isArray(valuesArray) && valuesArray.length) {
    store.dispatch({ type: 'SET_FIELD_VALIDATION', name, valid: true });
  } else if (Array.isArray(valuesArray) && valuesArray.length === 0) {
    store.dispatch({ type: 'SET_FIELD_VALIDATION', name, valid: false });

  // if the field is not required and is blank, unset it's validation key
  } else if (!required && (value === '' || value === undefined || value === null)) {
    store.dispatch({ type: 'SET_FIELD_VALIDATION', name, valid: undefined });

  // else if normal value, do normal validation
  } else if (isValid({ value, validationRegex })) {
    store.dispatch({ type: 'SET_FIELD_VALIDATION', name, valid: true });
  } else {
    store.dispatch({ type: 'SET_FIELD_VALIDATION', name, valid: false });
  }
}

let saveTimer = null;

export function handleChange({
  value,
  valuesArray,
  name,
  valid,
  validationRegex,
  forceValidation,
  required,
  unsetFieldsOnTrue = [],
  unsetFieldsOnFalse = [],
  unsetFieldsOnCondition = {},
  // eslint-disable-next-line
  onChangeCallback = (newValue) => {}, // callback is passed the changed value
}) {
  if (Array.isArray(valuesArray)) {
    store.dispatch({ type: 'SET_FIELD_VALUE', name, value: valuesArray });
  } else {
    store.dispatch({ type: 'SET_FIELD_VALUE', name, value });
  }
  // update field validation on every change only after
  // field validation has been initially set by the first
  // onBlur event (or optionally force it with an arg).
  if (valid !== undefined || forceValidation) {
    // eslint-disable-next-line
    handleValidation({ value, valuesArray, validationRegex, name, required });
  }
  // run callbacks
  unsetFieldsOnTrue.forEach((field) => {
    if (value === true) {
      store.dispatch({ type: 'SET_FIELD_VALUE', name: field, value: undefined });
      store.dispatch({ type: 'SET_FIELD_VALIDATION', name: field, value: undefined });
    }
  });
  unsetFieldsOnFalse.forEach((field) => {
    if (value === false) {
      store.dispatch({ type: 'SET_FIELD_VALUE', name: field, value: undefined });
      store.dispatch({ type: 'SET_FIELD_VALIDATION', name: field, value: undefined });
    }
  });
  if (unsetFieldsOnCondition.condition) {
    unsetFieldsOnCondition.fieldsToUnset.forEach((field) => {
      store.dispatch({ type: 'SET_FIELD_VALUE', name: field, value: undefined });
      store.dispatch({ type: 'SET_FIELD_VALIDATION', name: field, value: undefined });
    });
  }
  onChangeCallback(value);

  const { values } = store.getState();
  window.clearInterval(saveTimer);
  saveTimer = setTimeout(() => updateEnrollment({ values }), 3000);
}

// used for receiving and posting our mmb-api enrollment objects
export function fmtEnrollment(e, fields, opts) {
  const options = {
    post: false,
    ...opts,
  };

  let fmt = {};
  if (options.post) {
    fmt = {
      quote_id: e.quoteID,
      carrier: e.carrier,
      // posting - these are evalues
      fields,
      submitted_flag: e.submittedFlag,
      submit_date: e.submitDate,
    };
  } else {
    fmt = {
      quoteID: e.quote_id,
      carrier: e.carrier,
      enrollmentID: e.enrollment_id,
      fields: e.fields,
      submittedFlag: e.submitted_flag,
      submitDate: e.submit_date,
      confirmationNumber: e.confirmation_number,
    };
  }
  return fmt;
}

export function isAepPeriod(year = '2022') {
  const aepStart = moment(`${year}-10-15`);
  const today = moment();
  const aepEnd = moment(`${year}-12-07`);
  return aepStart <= today && today <= aepEnd;
}

export function canEnrollCarrier({ carrier, planYear }) {
  const { enrollCarriers } = store.getState().theme;

  if (Array.isArray(enrollCarriers)) {
    if (enrollCarriers.includes(carrier)) return true;
  } else {
    // if `carriersWithPricing` is not specified or false in the theme,
    // then assume all plans have pricing.
    if (!enrollCarriers) return true;

    // if it is specified, in theme
    const validCarriers = Object.keys(enrollCarriers);
    const validYears = enrollCarriers[carrier] || [];
    if (validCarriers.includes(carrier) && validYears.includes(planYear)) {
      return true;
    }
    return false;
  }
  return false;
}
